import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, finalize, map, Observable, tap } from 'rxjs';

import {
  ExternalProductTemplateCategory,
  ExternalProductTemplate,
  ExternalProductTemplateSupplier,
  PaginatedList,
  ProductTemplate,
  ProductTemplateProviderType,
  Result,
} from '@shared/models';

import { BaseService } from '../base.service';
import { httpParams } from '../http-params.builder';
import { LoadersService } from '../loaders.service';
import { Filter, Sort } from '../table-filters.service';

@Injectable({
  providedIn: 'root',
})
export class ProductTemplateService extends BaseService {
  constructor(
    private http: HttpClient,
    private loadersService: LoadersService
  ) {
    super();
  }

  getAll(loader: string, projectId: string | undefined, onlyExternal = false): Observable<ProductTemplate[]> {
    this.loadersService.show(loader);

    const url =
      projectId && !onlyExternal
        ? `${this.baseUrl}/product-templates/project/${projectId}`
        : `${this.baseUrl}/product-templates`;

    return this.http
      .get<ProductTemplate[]>(url, { params: { onlyExternal } })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getByIds(loader: string, ids: string[]): Observable<ProductTemplate[]> {
    this.loadersService.show(loader);
    return this.http
      .get<ProductTemplate[]>(`${this.baseUrl}/product-templates/by-ids`, {
        params: { ids },
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getByReferenceCodes(loader: string, referenceCodes: string[], projectId?: string): Observable<ProductTemplate[]> {
    this.loadersService.show(loader);
    return this.http
      .get<ProductTemplate[]>(`${this.baseUrl}/product-templates/by-codes`, {
        params: { referenceCodes, ...(projectId ? { projectId } : {}) },
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  get(
    loader: string,
    projectId: string | undefined,
    pageIndex = 0,
    pageSize = 50,
    filters: Filter = null,
    sort: Sort = null,
    onlyExternal = false
  ): Observable<PaginatedList<ProductTemplate>> {
    this.loadersService.show(loader);

    const url =
      projectId && !onlyExternal
        ? `${this.baseUrl}/product-templates/paginated/project/${projectId}`
        : `${this.baseUrl}/product-templates/paginated`;

    return this.http
      .get<
        PaginatedList<ProductTemplate>
      >(url, { params: httpParams().paging(pageIndex, pageSize).table(filters, sort).set('onlyExternal', onlyExternal).build() })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  add(loader: string, dto: ProductTemplate): Observable<ProductTemplate> {
    this.loadersService.show(loader);
    return this.http
      .post<ProductTemplate>(this.baseUrl + '/product-templates', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  edit(loader: string, dto: ProductTemplate): Observable<ProductTemplate> {
    this.loadersService.show(loader);
    return this.http
      .put<ProductTemplate>(this.baseUrl + '/product-templates', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  delete(loader: string, id: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.delete(`${this.baseUrl}/product-templates/${id}`, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  propagateProductToTemplate(loader: string, productId: string): Observable<ProductTemplate> {
    this.loadersService.show(loader);
    return this.http
      .post<ProductTemplate>(`${this.baseUrl}/product-templates/propagate-product-to-template/${productId}`, undefined)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  import(loader: string, file: File, projectId: string | undefined): Observable<string[]> {
    this.loadersService.show(loader, 1);
    const formData = new FormData();
    formData.append('', file, file.name);
    const headers = new HttpHeaders({ 'ngsw-bypass': '' });

    const url = projectId
      ? `${this.baseUrl}/product-templates/import/project/${projectId}`
      : `${this.baseUrl}/product-templates/import`;

    return this.http
      .post(url, formData, {
        reportProgress: true,
        observe: 'events',
        headers: headers,
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.loadersService.update(loader, Math.round((100 * event.loaded) / event.total));
          }
        }),
        filter((event): event is HttpResponse<string[]> => event.type === HttpEventType.Response),
        map((response) => response.body as string[]),
        finalize(() => this.loadersService.hide(loader))
      );
  }

  parse(loader: string, file: File, projectId: string | undefined): Observable<Result<ProductTemplate>[]> {
    this.loadersService.show(loader, 1);
    const formData = new FormData();
    formData.append('', file, file.name);
    const headers = new HttpHeaders({ 'ngsw-bypass': '' });

    const url = projectId
      ? `${this.baseUrl}/product-templates/parse/project/${projectId}`
      : `${this.baseUrl}/product-templates/parse`;

    return this.http
      .post(url, formData, {
        reportProgress: true,
        observe: 'events',
        headers: headers,
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.loadersService.update(loader, Math.round((100 * event.loaded) / event.total));
          }
        }),
        filter((event): event is HttpResponse<Result<ProductTemplate>[]> => event.type === HttpEventType.Response),
        map((response) => response.body as Result<ProductTemplate>[]),
        finalize(() => this.loadersService.hide(loader))
      );
  }

  getExternalProductTemplates(
    loader: string,
    pageIndex = 0,
    pageSize = 50,
    filters: Filter = null,
    sort: Sort = null
  ): Observable<PaginatedList<ExternalProductTemplate>> {
    this.loadersService.show(loader);
    return this.http
      .get<PaginatedList<ExternalProductTemplate>>(`${this.baseUrl}/product-templates/external`, {
        params: httpParams().paging(pageIndex, pageSize).table(filters, sort).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getExternalProductTemplateCategories(loader: string): Observable<Result<ExternalProductTemplateCategory[]>> {
    this.loadersService.show(loader);
    return this.http
      .get<Result<ExternalProductTemplateCategory[]>>(`${this.baseUrl}/product-templates/external/categories`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getExternalProductTemplateSuppliers(loader: string): Observable<Result<ExternalProductTemplateSupplier[]>> {
    this.loadersService.show(loader);
    return this.http
      .get<Result<ExternalProductTemplateSupplier[]>>(`${this.baseUrl}/product-templates/external/suppliers`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  addOrUpdateParsedItems(
    loader: string,
    items: { items: (ProductTemplate | ExternalProductTemplate)[] }
  ): Observable<string[]> {
    this.loadersService.show(loader);
    return this.http
      .put<string[]>(this.baseUrl + '/product-templates/parse/import', items)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  importExternalProductTemplates(loader: string, command: { items: ExternalProductTemplate[] }): Observable<string[]> {
    this.loadersService.show(loader);
    return this.http
      .put<string[]>(this.baseUrl + '/product-templates/external/import', command)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  syncExternalProductTemplates(loader: string, provider: ProductTemplateProviderType): Observable<number> {
    this.loadersService.show(loader);
    const providerName = {
      [ProductTemplateProviderType.Nobb]: 'nobb',
      [ProductTemplateProviderType.Heidenreich]: 'heidenreich',
      [ProductTemplateProviderType.Ahlsell]: 'ahlsell',
      [ProductTemplateProviderType.BerggardAmundsen]: 'berggard-amundsen',
      [ProductTemplateProviderType.Logiq]: 'logiq',
      [ProductTemplateProviderType.Solar]: 'solar',
      [ProductTemplateProviderType.Grunndata]: 'grunndata',
    }[provider];

    return this.http
      .post<number>(`${this.baseUrl}/product-templates/external/suppliers/sync/${providerName}`, undefined)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  mergeExternalProductTemplates(loader: string): Observable<number> {
    this.loadersService.show(loader);
    return this.http
      .post<number>(`${this.baseUrl}/product-templates/external/merge`, undefined)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  mergeCompanyExternalProductTemplates(loader: string): Observable<number> {
    this.loadersService.show(loader);
    return this.http
      .post<number>(`${this.baseUrl}/product-templates/external/merge/company`, undefined)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  exportEmpty(loader: string) {
    this.loadersService.show(loader);
    return this.http
      .get(`${this.baseUrl}/product-templates/export/empty`, { responseType: 'blob' })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  export(loader: string, projectId: string | undefined) {
    this.loadersService.show(loader);

    const url = projectId
      ? `${this.baseUrl}/product-templates/export/project/${projectId}`
      : `${this.baseUrl}/product-templates/export`;

    return this.http.get(url, { responseType: 'blob' }).pipe(finalize(() => this.loadersService.hide(loader)));
  }
}
